/* eslint-disable no-underscore-dangle */
import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  InMemoryCache,
  createHttpLink,
  split
} from '@apollo/client';
import type { HttpOptions, NormalizedCacheObject } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { RetryLink } from '@apollo/client/link/retry';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { getState, select } from '@ps/web-core-store';
import { canUseDOM, getWindow, notEmpty } from '@ps/web-core-utils';
import { createClient } from 'graphql-ws';
import type React from 'react';
import { useMemo } from 'react';

import { useApolloServerContext } from './ApolloServerProvider';

declare global {
  interface Window {
    __APOLLO_STATE__?: NormalizedCacheObject;
  }
}

interface Props {
  httpLinkUri: HttpOptions['uri'];
  wsLinkUrl?: string;
  httpHeaders?: Record<string, unknown>;
  useIdpAuth?: boolean;
  ssrMode?: boolean;
  idpToken?: string;
}

interface OAuthTokens {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

const getToken = () => {
  const rootState = getState();
  const { idpToken } = select.session.get(rootState);

  if (!idpToken) {
    const storedData = sessionStorage.getItem('oauthTokens');
    const parsedData: Partial<OAuthTokens> = storedData
      ? (JSON.parse(storedData) as Partial<OAuthTokens>)
      : {};
    return parsedData?.access_token;
  }
  return idpToken;
};

const getApolloClient = ({
  httpLinkUri,
  wsLinkUrl,
  httpHeaders = {},
  useIdpAuth = false,
  ssrMode = false,
  idpToken
}: Props) => {
  const apolloInitialState = getWindow()?.__APOLLO_STATE__;
  const retryLink = new RetryLink({
    delay: { initial: 5000 }
  });
  const shouldRestoreState = canUseDOM && ssrMode && notEmpty(apolloInitialState);

  let link = createHttpLink({
    uri: httpLinkUri,
    fetchOptions: { cache: ssrMode ? 'no-store' : 'default' }
  });

  if (canUseDOM) delete window.__APOLLO_STATE__;

  if (useIdpAuth) {
    const authLink = setContext((_, { headers }) => ({
      headers: { ...headers, ...httpHeaders, Authorization: `Bearer ${idpToken || getToken()}` }
    }));
    link = authLink.concat(link);
  }

  if (canUseDOM && wsLinkUrl) {
    const wsLink = new GraphQLWsLink(
      createClient({
        url: wsLinkUrl,
        keepAlive: 40000,
        connectionParams: async () => {
          let params = {};
          if (useIdpAuth) {
            params = {
              ...params,
              Authorization: `Bearer ${idpToken || getToken()}`
            };
          }
          if (httpHeaders) {
            params = {
              ...params,
              ...httpHeaders
            };
          }
          return params;
        }
      })
    );

    link = split(
      ({ query }) => {
        const definition = getMainDefinition(query);
        return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
      },
      wsLink,
      link
    );
  }

  const links = ApolloLink.from([retryLink, link]);

  return new ApolloClient({
    link: links,
    cache: new InMemoryCache().restore(shouldRestoreState ? apolloInitialState : {}),
    ssrMode: !canUseDOM && ssrMode,
    ssrForceFetchDelay: 100
  });
};

const ApolloClientProvider: React.FC<React.PropsWithChildren<Props>> = ({
  children,
  httpLinkUri,
  wsLinkUrl,
  httpHeaders,
  useIdpAuth,
  ssrMode,
  idpToken
}) => {
  const apolloServerContext = useApolloServerContext();
  const client = useMemo(
    () => getApolloClient({ httpLinkUri, wsLinkUrl, httpHeaders, useIdpAuth, ssrMode, idpToken }),
    [httpLinkUri, wsLinkUrl, httpHeaders, useIdpAuth, ssrMode, idpToken]
  );

  ssrMode && apolloServerContext.setClient?.(client);

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

export { ApolloClientProvider as default, getApolloClient };
